import React from 'react';

/**
 * App-wide safety net. A React app with no error boundary unmounts its whole
 * tree on any uncaught render/lifecycle error — the user just sees a blank white
 * screen with no clue what happened. This catches that, shows a readable message
 * (with the actual error, so it can be reported), and offers a reload instead of
 * leaving the page blank.
 */
interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<React.PropsWithChildren, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Surface it in the console so it shows up in reports / dev tools.
    // eslint-disable-next-line no-console
    console.error('Unhandled UI error:', error, info?.componentStack);
  }

  handleReload = () => {
    // Clear the error first so a client-side recovery is possible; fall back to
    // a hard reload of the current view.
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div style={{
        minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: '0.75rem', padding: '2rem', textAlign: 'center',
        fontFamily: 'system-ui, sans-serif',
      }}>
        <div style={{ fontSize: '2rem' }}>⚠️</div>
        <h2 style={{ margin: 0, color: '#0f172a', fontSize: '1.15rem', fontWeight: 700 }}>
          Something went wrong on this screen
        </h2>
        <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem', maxWidth: 460, lineHeight: 1.6 }}>
          The page hit an unexpected error and couldn’t finish loading. Your data is safe —
          reloading usually fixes it. If it keeps happening, please share the message below.
        </p>
        {this.state.error?.message && (
          <pre style={{
            margin: '0.25rem 0 0', maxWidth: 560, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.6rem 0.8rem',
            fontSize: '0.75rem', color: '#b91c1c', textAlign: 'left',
          }}>
            {this.state.error.message}
          </pre>
        )}
        <button onClick={this.handleReload} style={{
          marginTop: '0.5rem', background: '#2563eb', color: 'white', border: 'none',
          borderRadius: 8, padding: '0.6rem 1.4rem', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer',
        }}>
          Reload
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
